/**
 * Вход и регистрация.
 *
 * Два способа, и они по-разному отвечают на вопрос «это точно вы».
 *
 * **Почта и пароль** — обычная форма. Письма с подтверждением нет, и у
 * этого есть цена, о которой сказано на самом экране: адрес никем не
 * проверен, а забытый пароль восстановить нечем.
 *
 * **Telegram** — человек уходит к боту, бот спрашивает «вас пытаются
 * впустить, это вы?», человек отвечает кнопкой. Пока он отвечает, экран
 * ждёт и спрашивает Core, чем кончилось. Ожидание конечно: запрос живёт
 * пять минут, и молчание считается отказом.
 *
 * Ошибки показываются словами Core, а не своими: там знают, что именно
 * не так, а здесь пришлось бы гадать по коду ответа.
 */

import { BrandLogo } from '../brand/BrandLogo.js';
import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { coreApi, CoreApiError, type TelegramLoginState } from '../api/client.js';
import { broughtFromGoogle, googleProblem, saveSession } from '../state/session.js';
import { AuthArcs } from './AuthArcs.js';

type Tab = 'signup' | 'signin';
type Method = 'telegram';

const TABS: ReadonlyArray<{ id: Tab; label: string }> = [
  { id: 'signup', label: 'Регистрация' },
  { id: 'signin', label: 'Вход' },
];

/** Как часто спрашивать, ответил ли человек боту. */
const POLL_MS = 1500;

export function AuthScreen(): JSX.Element {
  const [tab, setTab] = useState<Tab>('signup');
  /** Какой способ раскрыт. Пусто — оба свёрнуты в кнопки. */
  const [method, setMethod] = useState<Method | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Доступен ли вход через Telegram. `null` — ещё не спросили. */
  const [telegramReady, setTelegramReady] = useState<boolean | null>(null);
  /** Настроен ли вход через Google. `null` — ещё не спросили. */
  const [googleReady, setGoogleReady] = useState<boolean | null>(null);
  const [waiting, setWaiting] = useState<{ link: string; state: TelegramLoginState } | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    coreApi
      .authState()
      .then((state) => {
        setTelegramReady(state.telegram);
        setGoogleReady(state.google);
      })
      // Сервер не ответил — кнопок не обещаем: нажатие привело бы в
      // никуда, а причина была бы не видна.
      .catch(() => {
        setTelegramReady(false);
        setGoogleReady(false);
      });
  }, []);

  /*
   * Возвращение от Google.
   *
   * Сервер привёл человека обратно с токеном в якоре адреса; сеанса из
   * него ещё нет — не хватает имени и аватарки. Спрашиваем «кто я» этим
   * же токеном и складываем сеанс целиком.
   */
  useEffect(() => {
    const problem = googleProblem();
    if (problem) {
      setError(problem === 'отменён' ? 'Вход через Google отменён' : 'Войти через Google не вышло');
      return;
    }

    const token = broughtFromGoogle();
    if (!token) return;

    setBusy(true);
    coreApi
      .me()
      .then(({ user }) => saveSession({ token, user }))
      .catch(() => setError('Войти через Google не вышло'))
      .finally(() => setBusy(false));
  }, []);

  // Опрос обязан прекращаться вместе с экраном: иначе он продолжит
  // стучаться в Core после входа.
  useEffect(
    () => () => {
      if (pollRef.current !== null) clearInterval(pollRef.current);
    },
    [],
  );

  const reset = (): void => {
    setMethod(null);
    setError(null);
    setWaiting(null);
    if (pollRef.current !== null) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const startTelegram = async (): Promise<void> => {
    setBusy(true);
    setError(null);

    try {
      const started = await coreApi.telegramStart();
      setMethod('telegram');
      setWaiting({ link: started.link, state: 'pending' });

      // Окно открывается сразу по нажатию: браузер блокирует всё, что
      // открыто позже, — он не отличает такое от рекламы.
      globalThis.open(started.link, '_blank', 'noopener');

      pollRef.current = globalThis.setInterval(() => {
        void coreApi
          .telegramResult(started.token)
          .then((result) => {
            if (result.state === 'pending') return;

            if (pollRef.current !== null) clearInterval(pollRef.current);
            pollRef.current = null;

            if (result.state === 'approved' && result.token && result.user) {
              saveSession({ token: result.token, user: result.user });
              return;
            }
            setWaiting((current) => (current ? { ...current, state: result.state } : null));
          })
          .catch(() => undefined);
      }, POLL_MS);
    } catch (cause) {
      setError(cause instanceof CoreApiError ? cause.message : 'Не удалось начать вход');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      {/*
        Дуги на фоне — рисунком, а не картинкой: градиент в четыреста
        килобайт PNG выглядел бы так же, но не подстроился бы ни под
        размер окна, ни под плотность экрана.
      */}
      <AuthArcs />

      <div className="auth-center">
        <header className="auth-brand">
          <h1 className="auth-logo">
            <BrandLogo layout="stack" />
          </h1>
          <p className="auth-tagline">Добро пожаловать в ANZONIX</p>
          <p className="auth-lead">Построй свой мир трейдинга</p>
        </header>

        <div className="auth-card">
          <div className="auth-tabs" role="tablist">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                className={tab === item.id ? 'auth-tab auth-tab--on' : 'auth-tab'}
                onClick={() => {
                  setTab(item.id);
                  reset();
                }}
              >
                {item.label}
              </button>
            ))}
            <span className={`auth-tab-line auth-tab-line--${tab}`} aria-hidden="true" />
          </div>

          {method === null && (
            <div className="auth-ways">
              <AuthWay
                tone="violet"
                label={tab === 'signup' ? 'Продолжить с Google' : 'Войти через Google'}
                icon={<GoogleIcon />}
                disabled={googleReady === false || busy}
                {...(googleReady === false ? { note: 'Вход через Google не настроен' } : {})}
                onClick={() => {
                  // Обычным переходом, а не запросом: человек должен
                  // увидеть в адресной строке accounts.google.com и замок
                  // — иначе это неотличимо от поддельного окна входа.
                  globalThis.location.href = '/api/auth/google/start';
                }}
              />
              <AuthWay
                tone="cyan"
                label={tab === 'signup' ? 'Продолжить через Telegram' : 'Войти через Telegram'}
                icon={<TelegramIcon />}
                disabled={telegramReady === false}
                {...(telegramReady === false
                  ? { note: 'Бот не подключён' }
                  : {})}
                onClick={() => void startTelegram()}
              />
            </div>
          )}

          {method === 'telegram' && waiting && (
            <div className="auth-wait">
              <TelegramWait state={waiting.state} link={waiting.link} />
              <div className="auth-form-row">
                <button type="button" className="auth-back" onClick={reset}>
                  Назад
                </button>
              </div>
            </div>
          )}

          {method === null && error && <p className="auth-error">{error}</p>}

          <div className="auth-or">
            <span>или</span>
          </div>

          <p className="auth-switch">
            {tab === 'signup' ? 'Уже есть аккаунт?' : 'Ещё нет аккаунта?'}{' '}
            <button
              type="button"
              className="auth-link"
              onClick={() => {
                setTab(tab === 'signup' ? 'signin' : 'signup');
                reset();
              }}
            >
              {tab === 'signup' ? 'Войти' : 'Создать'}
            </button>
          </p>
        </div>

        {/*
          Про восстановление пароля сказано заранее.

          Писем мы не шлём, и это значит, что забытый пароль вернуть
          нечем. Узнать об этом в момент, когда он уже забыт, — худший
          из возможных вариантов.
        */}
        <p className="auth-note">
          Писем мы не отправляем: адрес нужен как имя входа. Пароль восстановить нельзя —
          сохраните его.
        </p>
      </div>
    </div>
  );
}

/** Что видно, пока человек отвечает боту. */
function TelegramWait({ state, link }: { state: TelegramLoginState; link: string }): JSX.Element {
  if (state === 'declined') {
    return (
      <p className="auth-wait-text">
        Вход отклонён. Если это были не вы — всё в порядке, никто не вошёл.
      </p>
    );
  }

  if (state === 'closed') {
    return (
      <p className="auth-wait-text">
        Регистрация новых пользователей пока закрыта. Если у вас уже есть учётная запись — войдите тем же Telegram, что и раньше.
      </p>
    );
  }

  if (state === 'expired') {
    return (
      <p className="auth-wait-text">
        Запрос истёк — он живёт пять минут. Начните заново.
      </p>
    );
  }

  return (
    <>
      <p className="auth-wait-text">
        Откройте бота и подтвердите вход. Ждём вашего ответа.
      </p>
      {/*
        Ссылка на случай, если окно не открылось: браузеры блокируют
        новые вкладки чаще, чем кажется.
      */}
      <a className="auth-link" href={link} target="_blank" rel="noopener noreferrer">
        Открыть Telegram
      </a>
    </>
  );
}

function AuthWay({
  tone,
  label,
  icon,
  note,
  disabled,
  onClick,
}: {
  tone: 'violet' | 'cyan';
  label: string;
  icon: JSX.Element;
  /** Почему кнопка недоступна. Молчащая серая кнопка объясняет хуже. */
  note?: string;
  disabled?: boolean;
  onClick(): void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`auth-way auth-way--${tone}`}
      onClick={onClick}
      disabled={disabled === true}
      {...(note ? { title: note } : {})}
    >
      <span className="auth-way-icon">{icon}</span>
      <span className="auth-way-label">
        {label}
        {note && <em className="auth-way-note">{note}</em>}
      </span>
      <Arrow />
    </button>
  );
}

function Arrow(): JSX.Element {
  return (
    <svg className="auth-arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12h13m0 0-5-5m5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Значок Google.
 *
 * Фирменная «G» в четыре цвета: человек узнаёт её раньше, чем прочтёт
 * подпись, и по ней же понимает, куда его отправят.
 */
function GoogleIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.5 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24z"
      />
      <path fill="#FBBC05" d="M5.4 14.4a7.2 7.2 0 0 1 0-4.6V6.7H1.4a12 12 0 0 0 0 10.8l4-3.1z" />
      <path
        fill="#EA4335"
        d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.7l4 3.1C6.3 6.9 8.9 4.8 12 4.8z"
      />
    </svg>
  );
}

function TelegramIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 4.5 2.8 11.4c-.6.2-.6 1 0 1.2l4.6 1.5 1.7 5c.2.6 1 .7 1.4.2l2.4-2.7 4.6 3.4c.5.4 1.2.1 1.3-.5L21.9 5.3c.1-.6-.4-1-1-.8Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="m9.1 14.1 9-7.6-6.5 9.2" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}
